import React, { useState } from "react";
import { toast } from 'react-toastify';
import { useQuery } from "@tanstack/react-query";
import { useQueryClient } from "@tanstack/react-query";

import axios from "axios";
import config from '../../config'

import '../../styles/popup.css';
import "../../styles/buttons.css";

import AddAddressForm from "./addAddressForm";
import AddPropertyForm from "./addPropertyForm";
import AddUnitForm from "./addUnitForm";
import useAuthenticate from "../authentication/authenticate.js";
import imageUpload from '../general/imageUpload.js';

import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Tooltip from "@mui/material/Tooltip";
import { message, Upload } from 'antd';
import { PlusOutlined } from '@ant-design/icons';

const MultiStepForm = ({ setFilterState }) => {
    const [isExistingPropertyOpen, setIsExistingPropertyOpen] = useState(false);
    const [isOverlayOpen, setIsOverlayOpen] = useState(false);
    const [isAddingUnit, setIsAddingUnit] = useState(false);
    const [addressId, setAddressId] = useState("");
    const [propertyId, setPropertyId] = useState("");
    const [unitId, setUnitId] = useState("");
    const [activeStep, setActiveStep] = useState(0);
    const [skipped, setSkipped] = useState(new Set());
    const [filterState, setFilterStateLocal] = useState(false);
    const [currentSubmitHandler, setCurrentSubmitHandler] = useState(null);

    const [propertyImages, setPropertyImages] = useState([]);
    const [unitImages, setUnitImages] = useState([]);

    const queryClient = useQueryClient();

    const { data: propertiesData, isLoading: isPropertiesLoading } = useQuery({
        queryKey: ['v2-properties'],
        queryFn: fetchProperties
    });

    const { user } = useAuthenticate();
    const activeAccountId = Object.values(user?.user?.active || {})[0];

    const uploadButton = (
        <button style={{ border: 0, background: 'none' }} type="button">
            <PlusOutlined />
            <div className="font-poppins" style={{ marginTop: 4 }}>Upload</div>
        </button>
    );

    const steps = [
        'Input address',
        'Input property',
        'Upload property image',
        'Input unit details',
        'Upload unit images',
    ];

    const toggleFilter = () => {
        const newState = !filterState;
        setFilterStateLocal(newState);
        setFilterState(newState);
    }

    const closePopup = () => {
        setIsOverlayOpen(false);
        setIsAddingUnit(false);
        setActiveStep(0);
    };

    const isStepOptional = (step) => {
        return step === 4;
    };

    const isStepSkipped = (step) => {
        return skipped.has(step);
    };

    const handleNext = async () => {
        let newSkipped = skipped;

        if (isStepSkipped(activeStep)) {
            newSkipped = new Set(newSkipped.values());
            newSkipped.delete(activeStep);
        }

        // If there's a submit handler for the current step, call it first
        if ([0, 1, 3].includes(activeStep) && typeof currentSubmitHandler === "function") {
            try {
                const shouldProceed = await currentSubmitHandler();
                // handler should return true/false or throw error
                if (!shouldProceed) return; // stop progression if validation fails
            } catch (error) {
                console.error("Step submission failed:", error);
                return; // don't advance if error happens
            }
        }

        if (activeStep === 2) {
            if (propertyImages.length === 0) {
                toast.error("You must upload at least one property image.");
                return;
            }

            try {
                const imageIds = await imageUpload(
                    propertyImages.map(f => ({ name: f.name, type: f.type, data: f })),
                    "property",
                    propertyId,
                    activeAccountId,
                    propertyImages.map(() => "Property image")
                );

                await axios.put(`${config.API_URL}/v2/property/${propertyId}`, {
                    images: imageIds
                }, {headers: {Authorization: `Bearer ${localStorage.getItem('accessToken')}`}});

                toast.success("Property image uploaded successfully!");
            } catch (err) {
                toast.error("Failed to upload property images.");
                console.error(err);
                return;
            }
        }

        if (activeStep === 4) {
            if (unitImages.length > 0) {
                try {
                    const unitImageIds = await imageUpload(
                        unitImages.map(f => ({ name: f.name, type: f.type, data: f })),
                        "unit",
                        propertyId,
                        activeAccountId,
                        unitImages.map(() => "Unit image")
                    );
                    await axios.put(`${config.API_URL}/v2/unit/${unitId}`, {
                        images: unitImageIds
                    }, {headers: {Authorization: `Bearer ${localStorage.getItem('accessToken')}`}});
                    toast.success("Unit image(s) uploaded successfully!");
                } catch (err) {
                    toast.error("Failed to upload unit images.");
                    console.error(err);
                    return;
                }
            }
        }

        if (activeStep === steps.length - 1) {
            // Reset overlay when finished
            setIsOverlayOpen(false);
            setActiveStep(0);
        } else {
            setActiveStep((prevActiveStep) => prevActiveStep + 1);
        }

        setSkipped(newSkipped);
    };

    const handleBack = () => {
        if (isAddingUnit && activeStep === 3) {
            return;
        }
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const handleSkip = () => {
        if (!isStepOptional(activeStep)) {
            throw new Error("You can't skip a step that isn't optional.");
        }

        // If skipping last step, send unit information and close
        if (activeStep === steps.length - 1) {
            handleUnitCreated(true);
            closePopup();
        }

        setActiveStep((prevActiveStep) => prevActiveStep + 1);
        setSkipped((prevSkipped) => {
            const newSkipped = new Set(prevSkipped.values());
            newSkipped.add(activeStep);
            return newSkipped;
        });
    };

    const handleAddressCreated = (id) => {
        setAddressId(id);
        console.log("Address ID:", id); // Debugging log
    };

    const handlePropertyCreated = (id) => {
        setPropertyId(id);
        console.log("Property ID:", id); // Debugging log
    };

    const handleUnitCreated = (id) => {
        setUnitId(id);
        console.log("Unit ID:", id); // Debugging log
        queryClient.invalidateQueries(['v2-units']);
    };

    return (
        <div>
            <div className="page-header">
                <button className={`filter-button ${filterState ? 'active' : ''}`} onClick={toggleFilter}>
                    <i className="fas fa-filter"></i>Filter
                </button>
                <div>
                    <div style={{ display: 'flex', gap: '5px', }}>
                        <button className="header-button" onClick={() => { setIsExistingPropertyOpen(true); setIsAddingUnit(true) }}>
                            <i className="fas fa-add"></i>Unit
                        </button>
                        <button className="header-button" onClick={() => { setIsOverlayOpen(true); setIsAddingUnit(false) }}>
                            <i className="fas fa-add"></i>Property
                        </button>
                    </div>
                </div>
            </div>
            {isExistingPropertyOpen && isAddingUnit && (
                <div className="popup-overlay">
                    <div className="popup-content">
                        <button onClick={() => setIsExistingPropertyOpen(false)} className="close-popup">
                            <i className="fas fa-times"></i>
                        </button>
                        <div>
                            <h3 style={{ marginBottom: '25px' }}>Select an Existing Property</h3>
                            {isPropertiesLoading ? (
                                <p>Loading properties...</p>
                            ) : (
                                <Autocomplete
                                    options={propertiesData || []}
                                    getOptionLabel={(option) => option.name}
                                    value={propertiesData.find(prop => prop._id === propertyId) || null}
                                    onChange={(event, newValue) => setPropertyId(newValue ? newValue._id : "")}
                                    sx={{ marginBottom: '1rem' }}
                                    renderInput={(params) => <TextField {...params} label="Property" required />}
                                />
                            )}
                            <button
                                onClick={() => { setIsExistingPropertyOpen(false); setIsOverlayOpen(true); setActiveStep(3) }}
                                disabled={!propertyId}
                                className="button"
                                style={{ marginBottom: '-10px' }}
                            >
                                Use Selected Property
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {isOverlayOpen && (
                <div className="popup-overlay">
                    <div className="popup-content">
                        <button onClick={closePopup} className="close-popup">
                            <i className="fas fa-times"></i>
                        </button>
                        <h2 className="text-2xl text-black">Add Property</h2>
                        <div>
                            <Stepper activeStep={activeStep} style={{ marginBottom: '30px' }} alternativeLabel>
                                {steps.map((label, index) => {
                                    const stepProps = {};
                                    const labelProps = {};
                                    if (isStepOptional(index)) {
                                        labelProps.optional = (
                                            <Typography variant="caption">Optional</Typography>
                                        );
                                    }
                                    if (isStepSkipped(index)) {
                                        stepProps.completed = false;
                                    }
                                    return (
                                        <Step
                                            key={label}
                                            {...stepProps}
                                            sx={{
                                                "& .MuiStepIcon-root.Mui-active": {
                                                    color: "#C86B2C",
                                                },
                                                "& .MuiStepIcon-root.Mui-completed": {
                                                    color: "#C86B2C",
                                                },
                                            }}>
                                            <StepLabel {...labelProps}>{label}</StepLabel>
                                        </Step>
                                    );
                                })}
                            </Stepper>
                            <div style={{ paddingLeft: '10px', paddingRight: '10px' }}>
                                {activeStep === 0 && (
                                    <AddAddressForm
                                        onNext={handleAddressCreated}
                                        setSubmitHandler={setCurrentSubmitHandler}
                                    />
                                )}
                                {activeStep === 1 && (
                                    <AddPropertyForm
                                        addressId={addressId}
                                        onNext={handlePropertyCreated}
                                        setSubmitHandler={setCurrentSubmitHandler}
                                    />
                                )}
                                {activeStep === 2 && (
                                    <div
                                        className="property-img-upload"
                                        style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}
                                    >
                                        <Upload
                                            name="avatar"
                                            listType="picture-card"
                                            className="property-uploader"
                                            showUploadList={false} // we’ll handle preview & delete ourselves
                                            beforeUpload={(file) => {
                                                const isJpgOrPng =
                                                    file.type === "image/jpeg" || file.type === "image/png";
                                                if (!isJpgOrPng) {
                                                    message.error("You can only upload JPG/PNG file!");
                                                    return Upload.LIST_IGNORE;
                                                }
                                                const isLt2M = file.size / 1024 / 1024 < 2;
                                                if (!isLt2M) {
                                                    message.error("Image must be smaller than 2MB!");
                                                    return Upload.LIST_IGNORE;
                                                }

                                                // Save to state — only 1 file allowed
                                                setPropertyImages([file]);

                                                return false; // prevent auto-upload
                                            }}
                                        >
                                            {propertyImages.length > 0 ? (
                                                <div style={{ position: "relative" }}>
                                                    <img
                                                        src={URL.createObjectURL(propertyImages[0])}
                                                        alt="property"
                                                        style={{ width: "100%" }}
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation(); // prevent re-triggering upload
                                                            setPropertyImages([]); // clear image
                                                        }}
                                                        style={{
                                                            position: "absolute",
                                                            top: 6,
                                                            right: 6,
                                                            background: "rgba(0,0,0,0.5)",
                                                            color: "#fff",
                                                            border: "none",
                                                            borderRadius: "50%",
                                                            cursor: "pointer",
                                                            width: 22,
                                                            height: 22,
                                                        }}
                                                    >
                                                        <i className="fas fa-times"></i>
                                                    </button>
                                                </div>
                                            ) : (
                                                uploadButton
                                            )}
                                        </Upload>
                                        <p className="font-poppins text-sm text-gray-500 mt-4 px-4" style={{ marginTop: "20px" }}>
                                            Required: Upload image of the property. JPG or PNG file (max 2MB).
                                        </p>
                                    </div>
                                )}
                                {activeStep === 3 && (
                                    <AddUnitForm
                                        propertyId={propertyId}
                                        onNext={handleUnitCreated}
                                        setSubmitHandler={setCurrentSubmitHandler}
                                    />
                                )}
                                {activeStep === 4 && (
                                    <div>
                                        <Upload
                                            multiple
                                            listType="picture-card"
                                            beforeUpload={(file) => {
                                                const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
                                                if (!isJpgOrPng) {
                                                    message.error('You can only upload JPG/PNG file!');
                                                    return Upload.LIST_IGNORE;
                                                }
                                                const isLt2M = file.size / 1024 / 1024 < 2;
                                                if (!isLt2M) {
                                                    message.error('Image must be smaller than 2MB!');
                                                    return Upload.LIST_IGNORE;
                                                }
                                                setUnitImages(prev => [...prev, file]);
                                                return false;
                                            }}
                                            onRemove={(file) => {
                                                setUnitImages(prev => prev.filter(f => f.uid !== file.uid));
                                            }}
                                            fileList={unitImages}
                                        >
                                            {uploadButton}
                                        </Upload>
                                        <div className="font-poppins text-sm text-gray-500 mt-4">
                                            Optional: Upload unit image(s). JPG/PNG, max 2MB
                                        </div>
                                    </div>
                                )}
                            </div>
                            <React.Fragment>
                                <Box sx={{ display: 'flex', flexDirection: 'row', pt: 4, paddingLeft: '10px', paddingRight: '10px' }}>
                                    <button
                                        className={`stepper-button ${activeStep === 0 || (isAddingUnit && activeStep === 3) ? "disabled" : ""}`}
                                        disabled={activeStep === 0 || (isAddingUnit && activeStep === 2)}
                                        onClick={handleBack}
                                    >
                                        Back
                                    </button>
                                    <Box sx={{ flex: '1 1 auto' }} />
                                    {isStepOptional(activeStep) && (
                                        <Button color="inherit" onClick={handleSkip} sx={{ mr: 1, textTransform: "none", borderRadius: "10px" }}>
                                            Skip
                                        </Button>
                                    )}
                                    <Tooltip
                                        title={activeStep === 4 && unitImages.length === 0 ? "Click Skip if you don’t want to add any images" : ""}
                                    >
                                        <span>
                                            <button
                                                className="stepper-button"
                                                onClick={handleNext}
                                                disabled={activeStep === 4 && unitImages.length === 0} // block Finish unless they upload OR Skip
                                            >
                                                {activeStep === steps.length - 1 ? 'Finish' : 'Next'}
                                            </button>
                                        </span>
                                    </Tooltip>
                                </Box>
                            </React.Fragment>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MultiStepForm;

const fetchProperties = async () => {
    try {
        const response = await axios.get(`${config.API_URL}/v2/property`, {
            headers: {Authorization: `Bearer ${localStorage.getItem('accessToken')}`},
        });
        return response.data;
    } catch (error) {
        console.error("Error fetching properties:", error);
        return [];
    }
}